import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { ajax } from 'rxjs/ajax';
import endpoints from 'actions/booking-endpoints';
import { buildHeaders } from 'actions/fetch-helper';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import { getDefaultErrorContext } from 'common/utils/rum-utils';
import { SERVICE_TYPE_FIELDS } from 'constants/booking-constants';
import {
  FETCH_QUOTE_V2,
  setPaymentMethodsAction,
} from 'actions/booking-actions';
import {
  saveSelectedCompanyAndGroup,
  getTempSavedUserProfileCompanies,
  getCompanyID,
  getUserCountryCode,
  parsePaxCompany,
  isUsingPaxPersonalPayments,
  matchSelectedCompanyAndGroup,
  firstValidCompanyAndGroup,
} from 'helper/user';
import { getCityStorage } from 'helper/storage';
import { pushErrorModalAction, setCompanyAndGroup } from 'actions/app-actions';
import { ERROR_MODAL_TYPE } from 'components/v2/error-modal/error-modal';
import {
  getPaxUserInfo,
  getPaxAccessToken,
  isPaxUser,
} from 'helper/grab-id-auth-helper';
import { redirectURL } from 'helper/redirect-helper';
import { isProduction } from 'common/utils/environment-util';
import {
  STG_LINK_PAX_APP_URL,
  PRD_LINK_PAX_APP_URL,
} from 'constants/app-constants';
import { buildAjaxCredentials } from '../../helper/ajax-helper';

export const fetchServiceTypes = (cityID, companyID) =>
  ajax({
    url: endpoints.fetchServiceTypesEpt(),
    method: 'POST',
    headers: buildHeaders(null, null, companyID, true),
    withCredentials: buildAjaxCredentials(),
    body: JSON.stringify({
      request: {
        queryParams: {
          IDS: [0], // ignore this
          includeInactive: false,
          cityID: Number(cityID),
        },
        fields: SERVICE_TYPE_FIELDS,
        companyID,
      },
    }),
  });

export const getOptimizedRoute = (payload, companyID) =>
  ajax({
    url: endpoints.getV2QuoteEpt(),
    method: 'POST',
    headers: buildHeaders(null, FETCH_QUOTE_V2, companyID, true),
    withCredentials: buildAjaxCredentials(),
    body: JSON.stringify(payload),
    timeout: 10000,
  });

export const fetchPaxServiceTypes = cityID =>
  ajax({
    url: endpoints.paxFetchServiceTypesEpt(),
    method: 'POST',
    headers: buildHeaders({
      type: 'Authorization',
      value: `Bearer ${getPaxAccessToken()}`,
    }),
    body: JSON.stringify({
      input: {
        fields: SERVICE_TYPE_FIELDS,
        queryParams: {
          IDS: [0],
          cityID: Number(cityID),
          includeInactive: false,
        },
      },
    }),
  });

export const fetchPaxPaymentMethods = countryCode => {
  const userInfo = getPaxUserInfo();
  return ajax({
    url: endpoints.paxFetchPaymentMethodsEpt(countryCode, userInfo.pid),
    method: 'GET',
    headers: buildHeaders({
      type: 'Authorization',
      value: `Bearer ${getPaxAccessToken()}`,
    }),
  });
};

export const fetchQuotes = (orders, companyID) =>
  ajax({
    url: endpoints.getQuotesEpt(),
    method: 'POST',
    headers: buildHeaders(null, FETCH_QUOTE_V2, companyID, true),
    withCredentials: buildAjaxCredentials(),
    body: JSON.stringify(orders),
  });

export const doAddressSearch = (keyword, companyID, countryCode, latlng) =>
  isPaxUser()
    ? ajax({
        url: endpoints.paxFetchSearchAddressEpt({
          keyword,
          countryCode,
          latlng,
        }),
        method: 'GET',
        headers: buildHeaders(null, null, null, true),
      })
    : ajax({
        url: endpoints.getAddressSearchEpt({ keyword, companyID, countryCode }),
        method: 'GET',
        headers: buildHeaders(null, null, companyID, true),
        withCredentials: buildAjaxCredentials(),
      });

export const extractCityFromSearchResults = (
  searchResults,
  cities,
  cityName,
  countryCode,
) => {
  const exactNameMatchLocation = searchResults.find(addressResult => {
    const cityResult = addressResult.address.city;
    return (
      !_isEmpty(cityResult) &&
      cityResult.toLowerCase() === cityName.toLowerCase()
    );
  });
  const approxNameMatchLocation = searchResults.find(addressResult => {
    const cityResult = addressResult.address.city;
    return (
      !_isEmpty(cityResult) &&
      cityResult.toLowerCase().includes(cityName.toLowerCase())
    );
  });

  const location =
    exactNameMatchLocation || approxNameMatchLocation || searchResults[0];

  const cityID = _get(location, 'address.cityId');
  const countryID = _get(location, 'address.country_id');
  const coordinates = _get(location, 'latlng');
  const country = _get(location, 'address.country');
  const { code: cityCode, poi: cityPOI, timezone, latitude, longitude } =
    cities.find(c => c.name === cityName) || {};
  const refCoordinates =
    latitude && longitude
      ? {
          latitude,
          longitude,
        }
      : undefined;
  return {
    cityID,
    city: cityName,
    coordinates,
    refCoordinates,
    countryID,
    country,
    cityCode,
    cityPOI,
    timezone,
    countryCode,
  };
};

// fetchedCompanies: for pax user, no need to use temp saved user profile companies
// fetchedCompanies is derived from PAX_FETCH_BUSINESS_PROFILES action
export const getInitialCompanyAndGroup = fetchedCompanies => {
  const companies = fetchedCompanies || getTempSavedUserProfileCompanies();
  let company = {};
  let group = {};
  const extraActions = [];

  // use selected company & group if alraedy stored previously
  const selectedCompanyAndGroup = matchSelectedCompanyAndGroup(companies);
  if (!_isEmpty(selectedCompanyAndGroup)) {
    company = selectedCompanyAndGroup.company;
    group = selectedCompanyAndGroup.group;
  } else {
    // look for first available company with a group if no default
    const validCompanyAndGroup = firstValidCompanyAndGroup(companies);
    if (!_isEmpty(validCompanyAndGroup)) {
      company = validCompanyAndGroup.company;
      group = validCompanyAndGroup.group;
      saveSelectedCompanyAndGroup(company.id, group.id);
      // set notification for new default company & group update
      extraActions.push(
        pushErrorModalAction({
          message: I18n.t(
            'v2.homepage.notification.account.defaultCompanyAndGroupUpdate.title',
          ),
          description: I18n.t(
            'v2.homepage.notification.account.defaultCompanyAndGroupUpdate.description',
            { company: company.name, group: group.name },
          ),
          type: ERROR_MODAL_TYPE.WARNING,
        }),
      );
    }
  }

  const hasValidCompanyAndGroup = !_isEmpty(company) && !_isEmpty(group);
  if (hasValidCompanyAndGroup) {
    if (isPaxUser() && !isUsingPaxPersonalPayments()) {
      company = parsePaxCompany(company);
      extraActions.push(setCompanyAndGroup(company, group));
      extraActions.push(setPaymentMethodsAction(company.paymentMethods));
    } else {
      extraActions.push(setCompanyAndGroup(company, group));
    }
  }

  return {
    company,
    group,
    extraActions,
  };
};

export const getInitAddressSearchParams = (userInfo, defaultCity) => {
  const companyID = getCompanyID(userInfo);
  const countryCode = getUserCountryCode(userInfo);
  const cityStorage = getCityStorage();

  const shouldUseStoredCity =
    countryCode === cityStorage.countryCode &&
    cityStorage.cityPOI &&
    cityStorage.city;

  const cityPOIToSearch = shouldUseStoredCity
    ? cityStorage.cityPOI
    : defaultCity.poi;

  const cityName = shouldUseStoredCity ? cityStorage.city : defaultCity.name;

  const { latitude = 0, longitude = 0 } = cityStorage.coordinates || {};
  const latlng = `${latitude},${longitude}`;

  return {
    companyID,
    countryCode,
    cityPOIToSearch,
    cityName,
    latlng,
  };
};

/**
 * Creates a pre-condition error modal action for PAX app linking
 * @param {string} errorType - The error modal type (WARNING, FAILED, etc.)
 * @param {string} epicName - Name of the epic that invoked this helper (for tracking)
 * @returns {Object} - pushErrorModalAction with pre-condition error details
 */
export const createPaxLinkPreConditionErrorModal = (
  errorType = ERROR_MODAL_TYPE.WARNING,
  epicName = 'unknown',
) => {
  const error = new Error('GFB account not linked to PAX app');
  const errorCtx = rumErrorCtx(getDefaultErrorContext())
    .component(`createPaxLinkPreConditionErrorModal`)
    .severity(SEVERITY.MEDIUM)
    .custom('triggeredBy', epicName)
    .build();
  ddRum.addError(error, errorCtx);

  return pushErrorModalAction({
    message: I18n.t('v2.error.preCondition.title'),
    description: I18n.t('v2.error.preCondition.description'),
    type: errorType,
    onOk: () =>
      redirectURL(isProduction() ? PRD_LINK_PAX_APP_URL : STG_LINK_PAX_APP_URL),
  });
};
